import { Doc } from '@dadohr/shared/domainTypes/document';
import { getItemDisplayName, resolveExpTitle } from '../../../Experiences/helpers/adminName';
import {
  ExperienceAnchorType,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import {
  CustomUserField,
  FREE_TIER_ORG_IDS,
  IOrganization
} from '@dadohr/shared/domainTypes/org';
import {
  PAAReportFilterApplied,
  PAEventType
} from '@dadohr/shared/domainTypes/productAnalytics';
import {
  IReportingQuery,
  ReportingFilter,
  ReportingFilterActor,
  ReportingFilterData,
  ReportingFilterRule,
  ReportingFilterTimelineItem
} from '@dadohr/shared/domainTypes/reports';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  ButtonGroup,
  IconButton,
  Popover,
  TextField
} from '@material-ui/core';
import { capitalize, compact, isEqual, sortBy } from 'lodash';
import { Edit, Trash2 } from 'lucide-react';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutocompleteMulti,
  AutocompleteSingle
} from '../../../../components/Autocomplete';
import {
  AutocompleteUserFieldMulti,
  AutocompleteUserMultiById
} from '../../../../components/AutocompleteUser';
import { WithShape } from '../../../../components/Charts/Util';
import {
  CustomInputLabel,
  MainLabel
} from '../../../../components/CustomInputLabel';
import { DateRangePicker } from '../../../../components/DateRangePicker';
import { InputFieldLoader } from '../../../../components/InputFieldLoader';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../layout/Flex';
import { COLOR_UNKNOWN } from '../../../../services/color';
import { useCurrentUser } from '../../../../services/currentUser';
import { useReportableExperienceTemplatesByOrgId } from '../../../../services/experiences/template';
import {
  useCombineLoadingValues,
  useMappedLoadingValue
} from '../../../../services/firebase';
import { useOrgById } from '../../../../services/orgs';
import { hasCurrentUserExperienceLevelScopes } from '../../../../services/permissions';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { fromMoment, ONE_DAY, toMoment } from '../../../../services/time';
import {
  toSearchTermRegexp,
  useUserFields,
  useUserRuleConfigWithAutocompletion,
  useUsersByOrgId
} from '../../../../services/user';
import { useActorDescriptorsFromTemplate } from '../../../Experiences/components/Actors';
import { useAnchorFields } from '../../../Experiences/components/Anchors';
import {
  EMPTY_RULE_GROUP,
  isEmptyRule,
  RuleBuilder
} from '../../../Experiences/components/RuleBuilder';
import {
  ScrollableBody,
  ScrollableContainer,
  StandardDrawer,
  StandardDrawerHeader
} from '../../../Experiences/components/StandardDrawer';

const ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'left'
} as const;

const ReportFilterContainer = styled('div')((p) => ({
  display: 'grid',
  gridColumnGap: p.theme.spacing(1),
  gridTemplateColumns: '1fr min-content',
  width: '100%'
}));

const ReportFilterActor = ({
  value,
  onChange,
  tpl
}: {
  value: ReportingFilterActor;
  onChange: (nextValue: ReportingFilterActor) => void;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );
  if (!value.actor) {
    return (
      <AutocompleteSingle
        label={t('Filter by actor')}
        fullWidth
        value={actorDescriptors[value.actor]}
        onChange={(nextActor) =>
          nextActor && onChange({ ...value, actor: nextActor.key })
        }
        options={Object.values(actorDescriptors)}
        getOptionSelected={(o) => o.key === value.actor}
        getOptionLabel={(o) => o.label}
        autoFocus={!value.actor}
        openOnFocus={true}
        renderOption={(a) => (
          <WithShape key={a.key} shape="square" color={a.color}>
            {a.label}
          </WithShape>
        )}
      />
    );
  }
  if (value.actor) {
    return (
      <AutocompleteUserMultiById
        orgId={tpl.data.orgId}
        fullWidth
        label={actorDescriptors[value.actor]?.label || value.actor}
        value={value.values}
        onChange={(nextUserIds) => onChange({ ...value, values: nextUserIds })}
        autoFocus={!value.values.length}
        openOnFocus={true}
      />
    );
  }
  return <div>{value.type}</div>;
};

const ReportFilterTimelineItem = ({
  value,
  onChange,
  tpl,
  removeComponent
}: {
  value: ReportingFilterTimelineItem;
  onChange: (nextValue: ReportingFilterTimelineItem) => void;
  tpl: Doc<IExperienceTemplate>;
  removeComponent: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );
  const [mode, setMode] = useState<'view' | 'edit'>(
    value.values.length ? 'view' : 'edit'
  );
  const options = useMemo(
    () =>
      sortBy(tpl.data.tasks, (tsk) => resolveExpTitle(getItemDisplayName(tsk), tpl.data.title)).map((tsk) => {
        return {
          id: tsk.id,
          title: resolveExpTitle(getItemDisplayName(tsk), tpl.data.title),
          color: actorDescriptors[tsk.actor]?.color || COLOR_UNKNOWN
        };
      }),
    [tpl, actorDescriptors]
  );
  const selectedOptions = options.filter((o) => value.values.includes(o.id));

  if (value.itemType !== 'TASK') {
    // not implemented yet
    return null;
  }

  if (mode === 'view') {
    return (
      <FlexContainerVertical
        marginBottom={1}
        role="div"
        onClick={() => setMode('edit')}
        style={{ cursor: 'pointer' }}
      >
        <FlexContainer justifyContent="space-between" fullWidth>
          <MainLabel>{t('Tasks')}</MainLabel>
          <IconButton color="primary">
            <Edit size={16} />
          </IconButton>
        </FlexContainer>
        <FlexContainerVertical fullWidth>
          {selectedOptions.map((o) => (
            <WithShape shape="square" color={o.color}>
              {o.title}
            </WithShape>
          ))}
        </FlexContainerVertical>
      </FlexContainerVertical>
    );
  }

  return (
    <>
      <AutocompleteMulti
        label={t('Tasks')}
        value={selectedOptions}
        fullWidth
        onChange={(nextSelected) =>
          onChange({
            ...value,
            values: nextSelected.map((x) => x.id)
          })
        }
        options={options}
        getOptionLabel={(o) => (
          <WithShape shape="square" color={o.color}>
            {o.title}
          </WithShape>
        )}
        getOptionSelected={(o, v) => o?.id === v?.id}
        filterOptions={(os, s) => {
          const re = toSearchTermRegexp(s.inputValue);
          return os.filter(
            (o) => !value.values.includes(o.id) && re.exec(o.title)
          );
        }}
        autoFocus={!selectedOptions.length}
        openOnFocus={true}
      />
      {removeComponent}
    </>
  );
};

const ReportFilterDataInner = ({
  value,
  onChange,
  customFields,
  users
}: {
  value: ReportingFilterData;
  onChange: (nextValue: ReportingFilterData) => void;
  customFields: CustomUserField[];
  users: Doc<IUser>[];
}) => {
  const { t } = useUnsafeTranslation();
  const fields = useUserFields(customFields);
  const textFields = useMemo(() => {
    return sortBy(
      fields.filter((f) => f.type === 'string'),
      (f) => f.label
    );
  }, [fields]);
  const selectedField =
    (value.field && textFields.find((f) => f.key === value.field)) || null;
  if (!selectedField) {
    return (
      <AutocompleteSingle
        value={selectedField}
        onChange={(nextField) =>
          nextField && onChange({ ...value, field: nextField.key })
        }
        options={textFields}
        getOptionSelected={(o) => o?.key === value.field}
        getOptionLabel={(o) => o?.label || ''}
        autoFocus={!value.field}
        openOnFocus
        fullWidth
      />
    );
  }
  return (
    <AutocompleteUserFieldMulti
      label={t('Select {{ field }}', { field: selectedField.label })}
      value={value.values}
      onChange={(nextValues) => onChange({ ...value, values: nextValues })}
      field={selectedField}
      users={users}
      autoFocus={!value.values.length}
      openOnFocus
      fullWidth
    />
  );
};

const ReportFilterData = ({
  value,
  onChange,
  tpl
}: {
  value: ReportingFilterData;
  onChange: (nextValue: ReportingFilterData) => void;
  tpl: Doc<IExperienceTemplate>;
}) => {
  // need org and users in this org to work properly
  const { t } = useUnsafeTranslation();
  const orgId = tpl.data.orgId;
  const [d] = useCombineLoadingValues(
    useOrgById(orgId),
    useUsersByOrgId(orgId)
  );
  const [org, users] = d || [null, null];
  const firstLabel = t('Filter by data field');
  if (!org || !users) {
    return <InputFieldLoader label={firstLabel} fullWidth />;
  }
  return (
    <ReportFilterDataInner
      value={value}
      onChange={onChange}
      // TODO Mixpanel Offboarding filtering
      customFields={org.data.customUserFields}
      users={users}
    />
  );
};

const ReportFilterRuleInner = ({
  value,
  onChange,
  org,
  tpl
}: {
  value: ReportingFilterRule;
  onChange: (nextValue: ReportingFilterRule) => void;
  org: IOrganization;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const config = useUserRuleConfigWithAutocompletion(
    org.id,
    org.customUserFields,
    tpl.data.tz,
    { orgId: org.id, experienceTemplateId: tpl.id }
  );
  return (
    <RuleBuilder
      value={value.rule}
      onChange={(nextRule) =>
        nextRule && onChange({ ...value, rule: nextRule })
      }
      config={config}
      nullable
      variant="compact"
    />
  );
};

const ReportFilterRule = ({
  value,
  onChange,
  tpl
}: {
  value: ReportingFilterRule;
  onChange: (nextValue: ReportingFilterRule) => void;
  tpl: Doc<IExperienceTemplate>;
}) => {
  // need org and users in this org to work properly
  const { t } = useUnsafeTranslation();
  const orgId = tpl.data.orgId;
  const [org] = useOrgById(orgId);
  const firstLabel = t('Filter by data field');
  if (!org) {
    return <InputFieldLoader label={firstLabel} fullWidth />;
  }
  return (
    <ReportFilterRuleInner
      value={value}
      onChange={onChange}
      org={org.data}
      tpl={tpl}
    />
  );
};

const ReportFilter = ({
  value,
  onChange,
  onRemove,
  tpl
}: {
  value: ReportingFilter;
  onChange: (nextValue: ReportingFilter) => void;
  onRemove: () => void;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const removeComponent = (
    <IconButton onClick={() => onRemove()}>
      <Trash2 size={16} />
    </IconButton>
  );
  return (
    <ReportFilterContainer>
      {value.type === 'actor' && (
        <>
          <ReportFilterActor value={value} onChange={onChange} tpl={tpl} />
          {removeComponent}
        </>
      )}
      {value.type === 'data' && (
        <>
          <ReportFilterData value={value} onChange={onChange} tpl={tpl} />
          {removeComponent}
        </>
      )}
      {value.type === 'rule' && (
        <>
          <ReportFilterRule value={value} onChange={onChange} tpl={tpl} />
          {removeComponent}
        </>
      )}
      {value.type === 'timelineItem' && (
        <ReportFilterTimelineItem
          value={value}
          onChange={onChange}
          tpl={tpl}
          removeComponent={removeComponent}
        />
      )}
    </ReportFilterContainer>
  );
};

const sortFilters = (filters: ReportingFilter[]) => {
  return sortBy(filters, (f) =>
    f.type === 'timelineItem' && f.itemType === 'TASK' ? 999 : 0
  );
};

// Remove incomplete filter definitions
const cleanupQuery = (q: IReportingQuery): IReportingQuery => {
  return {
    ...q,
    filters: q.filters.filter((f) => {
      if (f.type === 'actor') {
        return f.actor && f.values.length;
      }
      if (f.type === 'timelineItem') {
        return f.itemType && f.values.length;
      }
      if (f.type === 'data') {
        return f.field && f.values.length; // need to figure out how to ask for empty specifically
      }
      if (f.type === 'rule') {
        return !isEmptyRule(f.rule);
      }
      return true;
    })
  };
};

export type ReportingQueryChange =
  | {
      type: 'Dates';
    }
  | {
      type: 'Experience';
    }
  | {
      type: 'Data Field';
      field: string;
      values: string[];
    }
  | {
      type: 'Rule';
      value: Rule<UserField>;
    }
  | {
      type: 'Actor';
      actor: string;
      values: string[];
    }
  | {
      type: 'Tasks';
      values: string[];
    }
  | {
      type: 'Task Assignees';
      value: string;
    };

const diffReportingFilters = (
  prev: IReportingQuery['filters'],
  next: IReportingQuery['filters']
): ReportingQueryChange[] => {
  if (!prev.length && !next.length) {
    return [];
  }
  const addedOrChangedFilters = next.filter((n) => {
    return !prev.find((p) => {
      if (p.type === 'actor' && n.type === 'actor') {
        if (p.actor === n.actor) {
          return p.condition === n.condition && isEqual(p.values, n.values);
        }
      }
      if (p.type === 'data' && n.type === 'data') {
        if (p.actor === n.actor) {
          if (p.field === n.field) {
            return p.condition === n.condition && isEqual(p.values, n.values);
          }
        }
      }
      if (p.type === 'rule' && n.type === 'rule') {
        return !isEqual(p.rule, n.rule);
      }
      if (p.type === 'timelineItem' && n.type === 'timelineItem') {
        if (p.itemType === n.itemType) {
          return p.condition === n.condition && isEqual(p.values, n.values);
        }
      }

      return false;
    });
  });

  return compact(
    addedOrChangedFilters.map((n) => {
      if (n.type === 'actor') {
        return {
          type: 'Actor',
          actor: n.actor,
          values: n.values
        };
      }
      if (n.type === 'timelineItem' && n.itemType === 'TASK') {
        return {
          type: 'Tasks',
          values: n.values
        };
      }
      if (n.type === 'data') {
        return {
          type: 'Data Field',
          actor: n.actor,
          field: n.field,
          values: n.values
        };
      }
      if (n.type === 'rule') {
        return {
          type: 'Rule',
          value: n.rule
        };
      }
      return null;
    })
  );
};

const diffReportingQuery = (prev: IReportingQuery, next: IReportingQuery) => {
  const changes: ReportingQueryChange[] = [];
  if (
    !prev.timeRange.start.isEqual(next.timeRange.start) ||
    !prev.timeRange.end.isEqual(next.timeRange.end)
  ) {
    changes.push({ type: 'Dates' });
  }
  if (!isEqual(prev.tplIds, next.tplIds)) {
    changes.push({ type: 'Experience' });
  }

  changes.push(...diffReportingFilters(prev.filters, next.filters));

  // HARDCODED STUFF FOR THE DRILLDOWN REPORTS!
  if (prev.otherData?.type !== next.otherData?.type && next.otherData?.type) {
    changes.push({ type: 'Task Assignees', value: prev.otherData?.type });
  }

  return changes;
};

const createPartialTrackingEvents = (
  prev: IReportingQuery,
  next: IReportingQuery
) => {
  const baseEvent: Omit<PAAReportFilterApplied, 'Report' | 'Filter Type'> = {
    eventType: PAEventType.EXPERIENCE_TPL,
    name: 'Report Filter Applied',
    'Experience Template ID': next.tplIds[0],
    Explanation: 'Admin user applied filters to a report',
    'Start Date': next.timeRange.start.toDate().toISOString(),
    'End Date': next.timeRange.end.toDate().toISOString(),
    'Size of Date Range': Math.floor(
      (next.timeRange.end.toMillis() - next.timeRange.start.toMillis()) /
        ONE_DAY
    )
  };
  const filterDiff = diffReportingQuery(prev, next);
  return compact(
    filterDiff.map<Omit<PAAReportFilterApplied, 'Report'> | null>((diff) => {
      if (diff.type === 'Dates') {
        return {
          ...baseEvent,
          'Filter Type': diff.type
        };
      }
      if (diff.type === 'Experience') {
        return {
          ...baseEvent,
          'Filter Type': diff.type
        };
      }
      if (diff.type === 'Actor') {
        return {
          ...baseEvent,
          'Filter Type': diff.type,
          Actor: diff.actor,
          'Actor Users': diff.values
        };
      }
      if (diff.type === 'Task Assignees') {
        return {
          ...baseEvent,
          'Filter Type': diff.type
        };
      }
      if (diff.type === 'Tasks') {
        return {
          ...baseEvent,
          'Filter Type': diff.type,
          'Task IDs': diff.values,
          'Number of Tasks': diff.values.length
        };
      }

      if (diff.type === 'Data Field') {
        return {
          ...baseEvent,
          'Filter Type': diff.type,
          'Data Field': diff.field,
          'Data Field Values': diff.values
        };
      }

      if (diff.type === 'Rule') {
        return {
          ...baseEvent,
          'Filter Type': diff.type,
          Rule: JSON.stringify(diff.value)
        };
      }

      return null;
    })
  );
};

// Gernot: I gave up a little bit here.
// In general this is a general purpose filter component which takes other additional elements
// that can be rendered with a nice generic type.
// This is all nice and works fine - the issue is that Mixpanel tracking is also
// interested in this and this is where I gave up a little bit here.
// Just hardcoded the element that's specific to one report in the diffFilter fn and the
// subsequent conversion to tracking events.
// It's not nice - but it's not worth making this more complicated just for the sake of getting
// one more piece of information onto these tracking events.
export const ReportFilters = <OtherData extends object = any>({
  value,
  onChange: _onChange,
  initialValue,
  additionalElements,
  additionalTrackingProps,
  provideDrawer
}: {
  value: IReportingQuery<OtherData>;
  onChange: (nextValue: IReportingQuery<OtherData>) => void;
  initialValue: IReportingQuery | void;
  additionalElements?: React.FunctionComponent<{
    state: IReportingQuery<OtherData>;
    setState: React.Dispatch<React.SetStateAction<IReportingQuery<OtherData>>>;
    selectedTpl: Doc<IExperienceTemplate> | null;
    isInSideBar: boolean;
  }>;
  provideDrawer?: boolean;
  additionalTrackingProps: (
    ev: Omit<PAAReportFilterApplied, 'Report'>,
    nextQ: IReportingQuery<OtherData>
  ) => PAAReportFilterApplied;
}) => {
  const drawer = useDialogState();
  const timeRangeRef = useRef<HTMLInputElement>();
  const timeRangeDrawerRef = useRef<HTMLInputElement>();
  const [timeRangePickerOpen, setTimeRangePickerOpen] = useState<boolean>(
    false
  );
  const [
    timeRangePickerDrawerOpen,
    setTimeRangePickerDrawerOpen
  ] = useState<boolean>(false);

  const readOnly = FREE_TIER_ORG_IDS.includes(value.orgId);

  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const { trackEvent } = useProductAnalytics();
  const [tpls] = useMappedLoadingValue(
    useReportableExperienceTemplatesByOrgId(value.orgId),
    (data) => {
      const accessibleTpls = data.filter((tpl) =>
        hasCurrentUserExperienceLevelScopes(currentUser, tpl.id, ['view'])
      );
      return sortBy(accessibleTpls, (d) => d.data.adminTitle || d.data.title);
    }
  );

  const anchorFields = useAnchorFields(org.customUserFields);
  const [state, setState] = useState(value);
  useEffect(() => setState(value), [value]);

  const selectedTpl = useMemo(
    () =>
      tpls?.find((tpl): boolean => {
        return tpl.id === state.tplIds[0];
      }),
    [tpls, state.tplIds]
  );

  const anchorField =
    selectedTpl?.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE
      ? selectedTpl?.data.anchor.field
      : '';

  const anchorLabel = anchorField
    ? capitalize(
        anchorFields.find((f) => f.field === anchorField)?.shortLabel(t) ||
          anchorField
      )
    : 'Experience start';

  const disabled =
    value.tplIds[0] === state.tplIds[0] &&
    value.timeRange.start.isEqual(state.timeRange.start) &&
    value.timeRange.end.isEqual(state.timeRange.end) &&
    value.filters === state.filters &&
    value.otherData === state.otherData;

  const removeAllFilters = () => {
    setState({ ...state, filters: [] });
  };
  const addFilter = (f: ReportingFilter) =>
    setState({ ...state, filters: [...state.filters, f] });

  const removeFilter = (index: number) => {
    const nextFilters = sortFilters([...state.filters]);
    nextFilters.splice(index, 1);
    setState({ ...state, filters: nextFilters });
  };

  const updateFilter = (index: number, nextFilter: ReportingFilter) => {
    const nextFilters = sortFilters([...state.filters]);
    nextFilters[index] = nextFilter;
    setState({ ...state, filters: nextFilters });
  };

  const selectTpl = (nextTpl: Doc<IExperienceTemplate> | null) => {
    nextTpl &&
      setState((s) => ({
        ...s,
        tplIds: [nextTpl.id],
        filters: s.filters.filter((f) => {
          if (f.type === 'timelineItem') {
            return false;
          }
          if (f.type === 'actor') {
            return (
              f.actor === PARTICIPANT_ACTOR_KEY ||
              !!nextTpl.data.supportingActors[f.actor]
            );
          }
          return true;
        })
      }));
  };

  const onChange = (nextValue: IReportingQuery) => {
    const cleanNextValue = cleanupQuery(nextValue);
    _onChange(cleanNextValue);
    const partialEvents = createPartialTrackingEvents(value, cleanNextValue);
    const fullEvents = partialEvents.map((ev) =>
      additionalTrackingProps(ev, cleanNextValue)
    );
    fullEvents.forEach((ev) => trackEvent(ev));
  };

  return (
    <>
      <FlexContainer marginBottom={2}>
        <AutocompleteSingle
          value={selectedTpl || null}
          options={tpls || []}
          label={t('Experience')}
          fullWidth
          disabledClearable
          maxWidth={350}
          getOptionLabel={(tpl) => tpl.data.adminTitle || tpl.data.title}
          onChange={(nextTpl) => selectTpl(nextTpl)}
        />
        <TextField
          id="standard-select-time"
          label={t(anchorLabel)}
          inputRef={timeRangeRef}
          style={{ maxWidth: 250 }}
          value={`${toMoment(state.timeRange.start).format(
            org.dateFormats.MMDDYYYY
          )} - ${toMoment(state.timeRange.end).format(
            org.dateFormats.MMDDYYYY
          )}`}
          onClick={
            readOnly
              ? undefined
              : () => setTimeRangePickerOpen(!timeRangePickerOpen)
          }
          fullWidth
        />
        <Popover
          anchorEl={timeRangeRef.current}
          open={timeRangePickerOpen}
          onClose={() => setTimeRangePickerOpen(false)}
          anchorOrigin={ANCHOR_ORIGIN}
        >
          <DateRangePicker
            value={{
              start: toMoment(state.timeRange.start),
              end: toMoment(state.timeRange.end)
            }}
            onChange={(e: { start: moment.Moment; end: moment.Moment }) =>
              setState((prevState) => ({
                ...prevState,
                timeRange: {
                  start: fromMoment(e.start),
                  end: fromMoment(e.end)
                }
              }))
            }
          />
        </Popover>

        {additionalElements &&
          createElement(additionalElements, {
            state,
            setState,
            selectedTpl: selectedTpl || null,
            isInSideBar: false
          })}

        <Button
          variant="contained"
          color="primary"
          disabled={disabled}
          onClick={() => onChange(state)}
        >
          {t('Apply')}
        </Button>

        {provideDrawer && (
          <FlexItem flex={1}>
            <FlexContainer fullWidth justifyContent="flex-end">
              <Button
                color="primary"
                size="small"
                endIcon={<Edit size={12} />}
                onClick={() => drawer.openDialog()}
              >
                {!state.filters.length && t('Add filters')}
                {state.filters.length === 1 &&
                  `+ ${t('{{ count }} filter', {
                    count: state.filters.length
                  })}`}
                {state.filters.length > 1 &&
                  `+ ${t('{{ count }} filters', {
                    count: state.filters.length
                  })}`}
              </Button>
            </FlexContainer>
          </FlexItem>
        )}

        {provideDrawer && (
          <StandardDrawer open={drawer.dialogOpen} onClose={drawer.closeDialog}>
            <ScrollableContainer hasFooter>
              <StandardDrawerHeader
                title={t('Filters')}
                onClose={drawer.closeDialog}
              />
              <ScrollableBody>
                <FlexContainerVertical fullWidth spacing={3} marginTop={1}>
                  <AutocompleteSingle
                    value={selectedTpl || null}
                    options={tpls || []}
                    label={t('Experience')}
                    fullWidth
                    disabledClearable
                    getOptionLabel={(tpl) =>
                      tpl.data.adminTitle || tpl.data.title
                    }
                    onChange={(nextTpl) => selectTpl(nextTpl)}
                  />

                  <TextField
                    id="standard-select-time"
                    label={t(anchorLabel)}
                    inputRef={timeRangeDrawerRef}
                    value={`${toMoment(state.timeRange.start).format(
                      org.dateFormats.MMDDYYYY
                    )} - ${toMoment(state.timeRange.end).format(
                      org.dateFormats.MMDDYYYY
                    )}`}
                    onClick={
                      readOnly
                        ? undefined
                        : () =>
                            setTimeRangePickerDrawerOpen(
                              !timeRangePickerDrawerOpen
                            )
                    }
                    fullWidth
                  />
                  <Popover
                    anchorEl={timeRangeDrawerRef.current}
                    open={timeRangePickerDrawerOpen}
                    onClose={() => setTimeRangePickerDrawerOpen(false)}
                    anchorOrigin={ANCHOR_ORIGIN}
                  >
                    <DateRangePicker
                      value={{
                        start: toMoment(state.timeRange.start),
                        end: toMoment(state.timeRange.end)
                      }}
                      onChange={(e: {
                        start: moment.Moment;
                        end: moment.Moment;
                      }) =>
                        setState((prevState) => ({
                          ...prevState,
                          timeRange: {
                            start: fromMoment(e.start),
                            end: fromMoment(e.end)
                          }
                        }))
                      }
                    />
                  </Popover>

                  {additionalElements &&
                    createElement(additionalElements, {
                      state,
                      setState,
                      selectedTpl: selectedTpl || null,
                      isInSideBar: true
                    })}

                  {selectedTpl &&
                    sortFilters(state.filters).map((f, i) => {
                      return (
                        <ReportFilter
                          key={i}
                          value={f}
                          onChange={(nf) => updateFilter(i, nf)}
                          onRemove={() => removeFilter(i)}
                          tpl={selectedTpl}
                        />
                      );
                    })}

                  {selectedTpl && (
                    <CustomInputLabel label={t('Add a filter')} fullWidth>
                      <ButtonGroup fullWidth>
                        <Button
                          onClick={() =>
                            addFilter({
                              type: 'rule',
                              actor: PARTICIPANT_ACTOR_KEY,
                              rule: EMPTY_RULE_GROUP
                            })
                          }
                        >
                          {t('Data field')}
                        </Button>
                        <Button
                          onClick={() =>
                            addFilter({
                              type: 'actor',
                              actor: '',
                              condition: 'in',
                              values: []
                            })
                          }
                        >
                          {t('Actor')}
                        </Button>
                        <Button
                          disabled={
                            !!state.filters.find(
                              (f) =>
                                f.type === 'timelineItem' &&
                                f.itemType === 'TASK'
                            )
                          }
                          onClick={() =>
                            addFilter({
                              type: 'timelineItem',
                              itemType: 'TASK',
                              condition: 'in',
                              values: []
                            })
                          }
                        >
                          {t('Task')}
                        </Button>
                      </ButtonGroup>
                    </CustomInputLabel>
                  )}
                </FlexContainerVertical>
              </ScrollableBody>
              <FlexContainer justifyContent="flex-end">
                <Button color="primary" onClick={() => removeAllFilters()}>
                  {t('Remove all filters')}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                  onClick={() => {
                    onChange(state);
                    drawer.closeDialog();
                  }}
                >
                  {t('Apply filters')}
                </Button>
              </FlexContainer>
            </ScrollableContainer>
          </StandardDrawer>
        )}
      </FlexContainer>
    </>
  );
};
